import React, { type ClipboardEventHandler, type PropsWithChildren } from 'react';
import { Box, xcss } from '@atlaskit/primitives';

export type CellWrapperProps = PropsWithChildren<{
	isEditable: boolean;
	testId?: string;
	hasPadding?: boolean;
	ariaLabel?: string;
	onCut?: ClipboardEventHandler<HTMLElement>;
	onCopy?: ClipboardEventHandler<HTMLElement>;
	onPaste?: ClipboardEventHandler<HTMLElement>;
}>;

export const WrapperCommon = (props: PropsWithChildren) => (
	<Box xcss={wrapperCommonStyles}>{props.children}</Box>
);

export const CellWrapper = ({
	isEditable,
	testId,
	children,
	hasPadding = true,
	ariaLabel,
	onCut,
	onCopy,
	onPaste,
}: CellWrapperProps) => (
	<Box
		xcss={[
			wrapperCommonStyles,
			cellWrapperStyles,
			isEditable && editableCellWrapperStyles,
			hasPadding && cellWrapperPaddingStyles,
		]}
		testId={testId}
		aria-label={ariaLabel}
		onCut={onCut}
		onCopy={onCopy}
		onPaste={onPaste}
	>
		{children}
	</Box>
);

export const TextCellWrapper = ({
	isEditable,
	testId,
	ariaLabel,
	children,
	onCut,
	onCopy,
	onPaste,
}: CellWrapperProps) => (
	<Box
		xcss={[textCellWrapperStyles, isEditable && editableTextCellWrapperStyles]}
		testId={testId}
		aria-label={ariaLabel}
		onCut={onCut}
		onCopy={onCopy}
		onPaste={onPaste}
	>
		{children}
	</Box>
);

const wrapperCommonStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	width: '100%',
	height: '100%',
	outline: 'none',
});

const cellWrapperStyles = xcss({
	boxSizing: 'border-box',
	borderRadius: 'border.radius',
});

const cellWrapperPaddingStyles = xcss({
	paddingLeft: 'space.100',
});

const editableCellWrapperStyles = xcss({ cursor: 'pointer' });

const textCellWrapperStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	width: '100%',
	height: '100%',
	outline: 'none',
	paddingTop: 'space.0',
	paddingBottom: 'space.0',
	paddingLeft: 'space.075',
	paddingRight: 'space.075',
	// @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'form' does not exist in type 'SafeCSSObject'
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	form: {
		display: 'flex',
		alignItems: 'center',
		height: '100%',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'[data-ds--text-field--input]': {
			height: '100%',
			outline: 'none',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'[data-ds--text-field--container], [data-ds--text-field--container]:focus-within': {
			outline: 'none',
			boxShadow: 'none',
			border: 'none',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	span: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
});

const editableTextCellWrapperStyles = xcss({ cursor: 'text' });
